import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import homeInmeet from '@/static/home-inmeet.png';
import homeAftermeet from '@/static/home-aftermeet.png';

/**
 * 首页组件 - Notion 风格
 * 欢迎文案 + 两列功能卡片 + 底部语录
 */
const Home: React.FC = () => {
  const navigate = useNavigate();
  const cardsRef = useRef<HTMLDivElement>(null);

  // 入场动画
  useEffect(() => {
    const cards = cardsRef.current?.querySelectorAll('.home-card');
    cards?.forEach((card, i) => {
      const el = card as HTMLElement;
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      setTimeout(() => {
        el.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 400 + i * 200);
    });
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center bg-white overflow-y-auto">
      {/* 欢迎文案区域 */}
      <section className="w-full max-w-5xl mt-24 [@media(max-height:800px)]:mt-16 [@media(max-height:700px)]:mt-8 [@media(max-height:600px)]:mt-4 mb-12 px-8 text-center">
        <span className="text-xs font-medium text-[#91918E] tracking-[0.2em] uppercase mb-4 block">
          Hi，又碰面了
        </span>
        <h1 className="text-5xl font-bold tracking-tight mb-6 text-[#37352F]">
          选择你要做的事
        </h1>
        <p className="text-[#73726E] text-lg font-light leading-relaxed max-w-xl mx-auto mt-[-8px]">
          记录不是目的，思考才是  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;  开会选左边，会后选右边。
        </p>
        
        {/* <p className="text-[#73726E] text-lg font-bold leading-relaxed max-w-xl mx-auto pt-8">
          正在开会去左边，会开完了选右边。
        </p> */}
      </section>

      {/* 功能卡片区域 */}
      <section ref={cardsRef} className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8 px-8 mb-24">
        {/* 实时会议卡片 */}
        <div className="home-card group relative bg-white border border-[#E9E9E7] rounded-3xl p-8 flex flex-col justify-between hover:-translate-y-1 transition-all duration-300 cursor-pointer"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)'; }}
        >
          <div>
            <div className="flex justify-between items-start mb-6">
              <div className="w-12 h-12 bg-zinc-50 border border-[#E9E9E7] text-black rounded-xl flex items-center justify-center">
                {/* 麦克风图标 */}
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                </svg>
              </div>
              <span className="px-2 py-1 bg-green-50 text-green-600 text-[10px] font-bold rounded uppercase tracking-tighter">
                Live Support
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-3 text-[#37352F]">实时会议</h2>
            <p className="text-[#73726E] text-sm mb-8 leading-relaxed">
              实时转文字，支持同步记笔记。
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-center gap-3 text-sm text-[#37352F]">
                <svg className="w-[18px] h-[18px] text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                实时记录
              </div>
              <div className="flex items-center gap-3 text-sm text-[#37352F]">
                <svg className="w-[18px] h-[18px] text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                融合笔记
              </div>
              <div className="flex items-center gap-3 text-sm text-[#37352F]">
                <svg className="w-[18px] h-[18px] text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                结束即得纪要
              </div>
            </div>
          </div>

          {/* 实时会议预览图 */}
          <div className="mb-8 aspect-[16/9] rounded-xl bg-[#F7F7F5] overflow-hidden">
            <img src={homeInmeet} alt="实时会议功能预览" className="w-full h-full object-cover" />
          </div>

          <button
            onClick={() => navigate('/inMeet')}
            className="w-full py-4 bg-black text-white rounded-2xl font-semibold text-sm hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 group cursor-pointer"
          >
            开始会议
            <svg className="w-[18px] h-[18px] group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </div>

        {/* 会后整理卡片 */}
        <div className="home-card group relative bg-white border border-[#E9E9E7] rounded-3xl p-8 flex flex-col justify-between hover:-translate-y-1 transition-all duration-300 cursor-pointer"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)'; }}
        >
          <div>
            <div className="flex justify-between items-start mb-6">
              <div className="w-12 h-12 bg-zinc-50 border border-[#E9E9E7] text-black rounded-xl flex items-center justify-center">
                {/* 文档图标 */}
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <span className="px-2 py-1 bg-purple-50 text-purple-600 text-[10px] font-bold rounded uppercase tracking-tighter">
                AI Synthesis
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-3 text-[#37352F]">会后整理</h2>
            <p className="text-[#73726E] text-sm mb-8 leading-relaxed">
              上传录音或文档来智能整理。
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-center gap-3 text-sm text-[#37352F]">
                <svg className="w-[18px] h-[18px] text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                多种处理模式
              </div>
              <div className="flex items-center gap-3 text-sm text-[#37352F]">
                <svg className="w-[18px] h-[18px] text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                支持上传多类型资料
              </div>
              {/* 占位行，保持两卡片高度一致 */}
              <div className="flex items-center gap-3 text-sm text-transparent select-none">
                占位
              </div>
            </div>
          </div>

          {/* 会后整理预览图 */}
          <div className="mb-8 aspect-[16/9] rounded-xl bg-[#F7F7F5] overflow-hidden">
            <img src={homeAftermeet} alt="会后整理功能预览" className="w-full h-full object-cover" />
          </div>

          <button
            onClick={() => navigate('/afterMeet')}
            className="w-full py-4 bg-white border-2 border-black text-black rounded-2xl font-semibold text-sm hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2 group cursor-pointer"
          >
            上传资料
            <svg className="w-[18px] h-[18px] group-hover:-translate-y-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </button>
        </div>
      </section>

      {/* 底部语录 */}
      <footer className="w-full max-w-5xl px-8 mb-16 text-center italic text-[#91918E] font-light text-sm">
        "好的笔记，是思考的轨迹，而非对话的复写。"
      </footer>
    </div>
  );
};

export default Home;
